'use client';

import { createDebug } from '@shared/debug';
import { useDebounce } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLoqateService } from '../LoqateServiceFactory';
import type { Address, LoqateFindItem } from '../types';

type UseAddressLookupProps = {
  value: string;
  onChange?: (value: string) => void;
  onAddressSelect?: (address: Address) => void;
};

function useAddressLookup({ value, onChange, onAddressSelect }: UseAddressLookupProps) {
  const debug = useMemo(() => createDebug({ isEnabled: true }), []);
  const [suggestions, setSuggestions] = useState<LoqateFindItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useTranslations('my_account.profile.profile_info.address_edit');
  // No special processing flag needed; keep logic simple

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Get the loqate service from the factory hook
  const loqateService = useLoqateService();

  // Use the existing useDebounce hook - ensure value is never undefined
  const debouncedValue = useDebounce(value || '', 300);

  // Memoize the fetch suggestions function to prevent unnecessary re-renders
  const fetchSuggestions = useCallback(async () => {
    if (debouncedValue && debouncedValue.length > 2) {
      setIsLoading(true);

      try {
        const results = await loqateService.findAddresses(debouncedValue);
        setSuggestions(results);
        if (results.length > 0) {
          setShowSuggestions(true);
        } else {
          // No results: close popover and show inline message
          setShowSuggestions(false);
          setError(t('no_address_found_toast'));
        }
      } catch (err) {
        debug.log(String(err));
        setError(t('failed_to_fetch_address_suggestions'));
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      // Clear internal error when input is empty or too short so RHF required error can show
      setError(null);
      setIsLoading(false);
    }
  }, [debouncedValue, loqateService, debug, t]);

  // Fetch suggestions when the debounced value changes
  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  // Intentionally do not close on outside click to prevent flicker and keep suggestions open

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  async function handleSelectSuggestion(suggestion: LoqateFindItem, e?: React.MouseEvent) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      setIsLoading(true);

      // Find results other than Address (Postcode, Building, Street, and so on) are containers.
      // Pass the id back to Find until a retrievable Address is returned.
      if (suggestion.Type !== 'Address') {
        const results = await loqateService.findAddresses(value || '', suggestion.Id);
        setSuggestions(results);
        // Keep suggestions open for further selection
        setShowSuggestions(true);
      }
      // If it's a final address (Type === "Address"), retrieve the full details
      else if (suggestion.Id) {
        const address = await loqateService.retrieveAddress(suggestion.Id);
        if (address) {
          // Update the input value
          if (onChange) {
            onChange(address.fullAddress);
          }

          // Notify parent component about the selected address
          if (onAddressSelect) {
            onAddressSelect(address);
          }

          // Close suggestions only for final address selection
          setShowSuggestions(false);
        }
      }
    } catch (err) {
      debug.error('Error handling suggestion selection:', err);
      setError('Failed to retrieve address details');
    } finally {
      setIsLoading(false);
    }
  }

  const handleFocus = () => {
    if (value && value.length > 2) {
      setShowSuggestions(true);
    }
  };

  const reset = () => {
    if (onChange) {
      onChange('');
    }
    setSuggestions([]);
    setShowSuggestions(false);
    setError(null);
  };

  return {
    suggestions,
    showSuggestions,
    setShowSuggestions,
    isLoading,
    error,
    inputRef,
    suggestionsRef,
    handleInputChange,
    handleSelectSuggestion,
    handleFocus,
    reset,
  };
}

export { useAddressLookup };
